/**
 * Step 5: Results Summary
 *
 * Shows upload results with detailed statistics and issue breakdown.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui/button';
import { Alert, AlertDescription } from '../../ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { Badge } from '../../ui/badge';
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Users,
  Clock,
  AlertCircle
} from 'lucide-react';

export default function ResultsSummaryStep({ results, onClose }) {
  const { t } = useTranslation();

  if (!results) {
    return (
      <Alert variant="destructive">
        <XCircle className="h-4 w-4" />
        <AlertDescription>
          {t('bulkUpload.steps.results.error.noResults')}
        </AlertDescription>
      </Alert>
    );
  }

  const {
    batch_id,
    total_rows,
    successful,
    with_warnings,
    with_errors,
    failed,
    issues = [],
    processing_time
  } = results;

  const successRate = total_rows > 0
    ? ((successful + with_warnings + with_errors) / total_rows) * 100
    : 0;

  // Group issues by severity
  const errors = issues.filter(issue => issue.severity === 'error');
  const warnings = issues.filter(issue => issue.severity === 'warning');

  // Get top 10 issues for display
  const topIssues = [...errors, ...warnings].slice(0, 10);

  return (
    <div className="space-y-6">
      {/* Success Header */}
      <div className="text-center py-6">
        <div className="flex justify-center mb-4">
          <div className="bg-green-100 p-4 rounded-full">
            <CheckCircle2 className="w-16 h-16 text-green-600" />
          </div>
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {t('bulkUpload.steps.results.title')}
        </h2>

        <p className="text-gray-600">
          {t('bulkUpload.steps.results.subtitle', {
            count: successful + with_warnings + with_errors,
            total: total_rows
          })}
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="flex items-center justify-between mb-2">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <div className="text-2xl font-bold text-green-900">{successful}</div>
          <div className="text-sm text-green-700">
            {t('bulkUpload.steps.results.stats.successful')}
          </div>
        </div>

        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
          <div className="flex items-center justify-between mb-2">
            <AlertTriangle className="w-8 h-8 text-yellow-600" />
          </div>
          <div className="text-2xl font-bold text-yellow-900">{with_warnings}</div>
          <div className="text-sm text-yellow-700">
            {t('bulkUpload.steps.results.stats.warnings')}
          </div>
        </div>

        <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
          <div className="flex items-center justify-between mb-2">
            <AlertCircle className="w-8 h-8 text-orange-600" />
          </div>
          <div className="text-2xl font-bold text-orange-900">{with_errors}</div>
          <div className="text-sm text-orange-700">
            {t('bulkUpload.steps.results.stats.errors')}
          </div>
        </div>

        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <div className="flex items-center justify-between mb-2">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <div className="text-2xl font-bold text-red-900">{failed}</div>
          <div className="text-sm text-red-700">
            {t('bulkUpload.steps.results.stats.failed')}
          </div>
        </div>
      </div>

      {/* Processing Details */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="grid grid-cols-3 gap-4">
          <div>
            <div className="flex items-center text-sm text-gray-600 mb-1">
              <Users className="w-4 h-4 mr-2" />
              {t('bulkUpload.steps.results.details.batchId')}
            </div>
            <div className="font-mono text-xs text-gray-900">
              {batch_id}
            </div>
          </div>

          <div>
            <div className="flex items-center text-sm text-gray-600 mb-1">
              <Clock className="w-4 h-4 mr-2" />
              {t('bulkUpload.steps.results.details.processingTime')}
            </div>
            <div className="font-semibold text-gray-900">
              {processing_time}s
            </div>
          </div>

          <div>
            <div className="flex items-center text-sm text-gray-600 mb-1">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {t('bulkUpload.steps.results.details.successRate')}
            </div>
            <div className="font-semibold text-gray-900">
              {successRate.toFixed(1)}%
            </div>
          </div>
        </div>
      </div>

      {/* Issues Summary */}
      {topIssues.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b">
            <h3 className="font-semibold text-gray-900">
              {t('bulkUpload.steps.results.issues.title')}
            </h3>
            <p className="text-xs text-gray-600">
              {t('bulkUpload.steps.results.issues.showing', {
                count: topIssues.length,
                total: issues.length
              })}
            </p>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">
                    {t('bulkUpload.steps.results.issues.row')}
                  </TableHead>
                  <TableHead>
                    {t('bulkUpload.steps.results.issues.field')}
                  </TableHead>
                  <TableHead>
                    {t('bulkUpload.steps.results.issues.severity')}
                  </TableHead>
                  <TableHead>
                    {t('bulkUpload.steps.results.issues.message')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topIssues.map((issue, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium">{issue.row}</TableCell>
                    <TableCell>
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                        {issue.field}
                      </code>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={issue.severity === 'error' ? 'destructive' : 'warning'}
                        className={
                          issue.severity === 'warning'
                            ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                            : ''
                        }
                      >
                        {issue.severity}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-md">
                      {issue.message}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {issues.length > 10 && (
            <div className="bg-gray-50 px-4 py-3 border-t text-sm text-gray-600 text-center">
              {t('bulkUpload.steps.results.issues.viewMore', {
                count: issues.length - 10
              })}
            </div>
          )}
        </div>
      )}

      {/* Next Steps */}
      {(with_warnings > 0 || with_errors > 0) && (
        <Alert className="border-blue-300 bg-blue-50">
          <AlertCircle className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            <div className="font-semibold mb-2">
              {t('bulkUpload.steps.results.nextSteps.title')}
            </div>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>{t('bulkUpload.steps.results.nextSteps.reviewIssues')}</li>
              <li>{t('bulkUpload.steps.results.nextSteps.filterStudents')}</li>
              <li>{t('bulkUpload.steps.results.nextSteps.editStudents')}</li>
              <li>{t('bulkUpload.steps.results.nextSteps.resolveIssues')}</li>
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Success Message (No Issues) */}
      {with_warnings === 0 && with_errors === 0 && failed === 0 && (
        <Alert className="border-green-300 bg-green-50">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            <div className="font-semibold">
              {t('bulkUpload.steps.results.perfect.title')}
            </div>
            <p className="text-sm mt-1">
              {t('bulkUpload.steps.results.perfect.message')}
            </p>
          </AlertDescription>
        </Alert>
      )}

      {/* Close Button */}
      <div className="flex justify-center pt-4">
        <Button onClick={onClose} size="lg" className="min-w-[200px]">
          {t('bulkUpload.steps.results.close')}
        </Button>
      </div>
    </div>
  );
}
